import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, Building2, Calendar, FileText, Headphones, ArrowLeft, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuthState } from "@/hooks/useAuthState";
import { isGuestUser, resolveUserType } from "@/lib/livePlanAccess";
import { authService } from "@/services/authService";
import { createCheckoutSession } from "@/services/subscriptionService";
import logo from "@/assets/live-stay-logo.png";

const BENEFICIOS = [
  {
    title: "Propriedades em qualquer resort",
    description: "Cadastre e gerencie imóveis em todos os empreendimentos parceiros.",
    icon: Building2,
  },
  {
    title: "Reservas e solicitações",
    description: "Receba, aprove ou rejeite solicitações de reserva em um só lugar.",
    icon: Calendar,
  },
  {
    title: "Documentos e contratos",
    description: "Acesso a modelos de contrato e documentos para suas locações.",
    icon: FileText,
  },
  {
    title: "Suporte prioritário",
    description: "Atendimento dedicado para proprietários do plano Live.",
    icon: Headphones,
  },
];

const formatCpfDisplay = (raw: string) => {
  const d = raw.replace(/\D/g, '');
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
};

const AssinarLive = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userId, user, userType, loading: authLoading } = useAuthState();
  const effectiveUserType = resolveUserType(userType, user);
  const [loading, setLoading] = useState<'monthly' | 'annual' | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual' | null>(null);
  const [cpf, setCpf] = useState("");
  const [address, setAddress] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.cpf) setCpf(typeof user.cpf === 'string' && user.cpf.length === 11 ? formatCpfDisplay(user.cpf) : user.cpf);
    if (user?.address) setAddress(user.address);
    if (user?.birth_date) setBirthDate(String(user.birth_date).slice(0, 10));
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (isGuestUser(effectiveUserType)) {
      toast({
        title: "Plano Live indisponível",
        description: "A assinatura do plano Live é exclusiva para proprietários.",
        variant: "destructive",
      });
      navigate("/minha-area", { replace: true });
    }
  }, [authLoading, effectiveUserType, navigate, toast]);

  const handleAssinarClick = (plan: 'monthly' | 'annual') => {
    setSelectedPlan(plan);
    setModalOpen(true);
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 11);
    setCpf(raw ? formatCpfDisplay(raw) : "");
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !selectedPlan) return;
    const cpfDigits = cpf.replace(/\D/g, '');
    if (cpfDigits.length !== 11) {
      toast({ title: "CPF inválido", description: "Informe um CPF com 11 dígitos.", variant: "destructive" });
      return;
    }
    if (!address.trim()) {
      toast({ title: "Endereço obrigatório", description: "Preencha o endereço.", variant: "destructive" });
      return;
    }
    if (!birthDate) {
      toast({ title: "Data de nascimento obrigatória", description: "Preencha a data de nascimento.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await authService.updateUser(userId, {
        cpf: cpfDigits,
        address: address.trim(),
        birth_date: birthDate,
      });
      const { url } = await createCheckoutSession(selectedPlan);
      window.location.href = url;
    } catch (err) {
      setSubmitting(false);
      toast({
        title: "Erro",
        description: err instanceof Error ? err.message : "Não foi possível continuar.",
        variant: "destructive",
      });
    }
  };

  const handleVoltar = () => {
    navigate("/minha-area");
  };

  if (authLoading || isGuestUser(effectiveUserType)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Hero com logo e título */}
      <header className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleVoltar}
            className="gap-2 text-muted-foreground hover:text-foreground"
            aria-label="Voltar para Minha Área"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Voltar</span>
          </Button>
          <img
            src={logo}
            alt="Live Stay"
            className="h-10 sm:h-12 w-auto object-contain"
          />
          <div className="w-20 sm:w-24" />
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Cabeçalho da oferta */}
        <section className="text-center mb-10 sm:mb-14">
          <Badge variant="secondary" className="mb-4 text-xs sm:text-sm px-3 py-1">
            Plano Live
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground tracking-tight mb-3">
            Assine o plano Live
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            Desbloqueie o gerenciamento completo de propriedades e reservas em todos os resorts da Live Stay.
          </p>
        </section>

        {/* Benefícios em grid */}
        <section className="mb-12 sm:mb-16">
          <h2 className="text-xl font-semibold text-center mb-6 text-foreground">
            O que você ganha com o Live
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {BENEFICIOS.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="border-2 border-slate-200/80 dark:border-slate-700/80 hover:border-primary/30 hover:shadow-md transition-all">
                  <CardHeader className="pb-2">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold">{item.title}</CardTitle>
                        <CardDescription className="text-sm mt-1">{item.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Preços */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-center mb-6 text-foreground">
            Escolha como assinar
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Card className="border-2 border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Mensal</CardTitle>
                <CardDescription>Renovação todo mês. Cancele quando quiser.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold">R$ 9,90</span>
                  <span className="text-muted-foreground">/mês</span>
                </div>
                <Button
                  className="w-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100"
                  variant="default"
                  size="lg"
                  disabled={loading !== null}
                  onClick={() => handleAssinarClick("monthly")}
                  aria-label="Assinar plano Live mensal"
                >
                  Assinar mensal
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-500 bg-orange-50/50 dark:bg-orange-950/20 overflow-hidden relative hover:shadow-lg transition-shadow">
              <Badge className="absolute top-4 right-4 bg-orange-500 hover:bg-orange-600 text-white border-0">
                Economia ~50%
              </Badge>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  Anual
                </CardTitle>
                <CardDescription>Melhor custo-benefício. Cancele quando quiser.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-orange-700 dark:text-orange-400">R$ 59,90</span>
                  <span className="text-muted-foreground">/ano</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Equivale a ~R$ 4,99/mês (12× R$ 9,90 = R$ 118,80)
                </p>
                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white border-0"
                  size="lg"
                  disabled={loading !== null}
                  onClick={() => handleAssinarClick("annual")}
                  aria-label="Assinar plano Live anual"
                >
                  Assinar anual
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Confiança */}
        <section className="text-center py-6 border-t border-slate-200 dark:border-slate-800">
          <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4 text-primary" />
            Pagamento seguro processado pelo nosso gateway.
          </p>
        </section>

        <div className="flex justify-center pt-4">
          <Button variant="ghost" onClick={handleVoltar} className="gap-2" aria-label="Voltar para Minha Área">
            <ArrowLeft className="h-4 w-4" />
            Voltar para Minha Área
          </Button>
        </div>
      </main>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="modal-desc">
          <DialogHeader>
            <DialogTitle>Dados para assinatura</DialogTitle>
            <DialogDescription id="modal-desc">
              Preencha os campos obrigatórios. Você poderá alterá-los depois em Meus Dados. Em seguida será redirecionado ao pagamento seguro na Stripe.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleModalSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="modal-cpf">CPF *</Label>
              <Input
                id="modal-cpf"
                value={cpf}
                onChange={handleCpfChange}
                placeholder="000.000.000-00"
                className="h-11"
                required
                maxLength={14}
                inputMode="numeric"
                autoComplete="off"
                aria-label="CPF"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="modal-address">Endereço *</Label>
              <Textarea
                id="modal-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Rua, número, bairro, cidade, estado, CEP"
                className="min-h-[80px] resize-y"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="modal-birth">Data de nascimento *</Label>
              <Input
                id="modal-birth"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="h-11"
                required
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="bg-orange-500 hover:bg-orange-600">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continuar para pagamento"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssinarLive;
